import React, { useEffect, useRef, useState, useCallback } from 'react';
import { tenantGoogleMapsLoader } from '../../utils/tenantGoogleMapsLoader';

interface AddressComponents {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (components: AddressComponents) => void;
  className?: string;
  placeholder?: string;
  required?: boolean;
}

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

declare global {
  interface Window {
    google: typeof google;
  }
}

/** Google FormattableText or plain string → display string */
function formattableTextToString(ft: unknown): string {
  if (ft == null) return '';
  if (typeof ft === 'string') return ft;
  if (typeof ft === 'object' && ft !== null && 'text' in ft && typeof (ft as { text: unknown }).text === 'string') {
    return (ft as { text: string }).text;
  }
  return String(ft);
}

function sortPredictionsCanadaFirst<T extends { description: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const aIsCanadian =
      a.description.includes(', ON,') ||
      a.description.includes(', BC,') ||
      a.description.includes(', AB,') ||
      a.description.includes(', QC,') ||
      a.description.includes(', NS,') ||
      a.description.includes(', NB,') ||
      a.description.includes(', MB,') ||
      a.description.includes(', SK,') ||
      a.description.includes(', NL,') ||
      a.description.includes(', PE,') ||
      a.description.includes(', NT,') ||
      a.description.includes(', YT,') ||
      a.description.includes(', NU,') ||
      a.description.includes(', Canada');

    const bIsCanadian =
      b.description.includes(', ON,') ||
      b.description.includes(', BC,') ||
      b.description.includes(', AB,') ||
      b.description.includes(', QC,') ||
      b.description.includes(', NS,') ||
      b.description.includes(', NB,') ||
      b.description.includes(', MB,') ||
      b.description.includes(', SK,') ||
      b.description.includes(', NL,') ||
      b.description.includes(', PE,') ||
      b.description.includes(', NT,') ||
      b.description.includes(', YT,') ||
      b.description.includes(', NU,') ||
      b.description.includes(', Canada');

    if (aIsCanadian && !bIsCanadian) return -1;
    if (!aIsCanadian && bIsCanadian) return 1;

    if (aIsCanadian && bIsCanadian) {
      const aIsOntario = a.description.includes(', ON,');
      const bIsOntario = b.description.includes(', ON,');
      if (aIsOntario && !bIsOntario) return -1;
      if (!aIsOntario && bIsOntario) return 1;

      const aIsKingston = a.description.toLowerCase().includes('kingston');
      const bIsKingston = b.description.toLowerCase().includes('kingston');
      if (aIsKingston && !bIsKingston) return -1;
      if (!aIsKingston && bIsKingston) return 1;
    }

    return 0;
  });
}

/** Place (New) + legacy PlacesServiceDetailResult */
function parseAddressFromGooglePlace(place: {
  address_components?: Array<{ long_name?: string; short_name?: string; types: string[] }>;
  addressComponents?: Array<{ longText?: string; shortText?: string; types: string[] }>;
  formatted_address?: string;
  formattedAddress?: string;
}): AddressComponents {
  const components: AddressComponents = {
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
  };

  const list = place.addressComponents ?? place.address_components;
  if (!list) {
    return components;
  }

  for (const component of list) {
    const types = component.types;
    const c = component as {
      longText?: string;
      long_name?: string;
      shortText?: string;
      short_name?: string;
    };
    const longName = c.longText ?? c.long_name ?? '';
    const shortName = c.shortText ?? c.short_name ?? '';

    if (types.includes('street_number')) {
      components.address = longName + ' ';
    } else if (types.includes('route')) {
      components.address += longName;
    } else if (types.includes('locality')) {
      components.city = longName;
    } else if (types.includes('administrative_area_level_1')) {
      components.province = shortName || longName;
    } else if (types.includes('postal_code')) {
      components.postalCode = longName;
    } else if (types.includes('country')) {
      components.country = longName;
    }
  }

  components.address = components.address.trim();
  return components;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  className = '',
  placeholder = 'Start typing address...',
  required = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [mapsBootstrapping, setMapsBootstrapping] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [userIsTyping, setUserIsTyping] = useState(false);
  const [suggestionProblem, setSuggestionProblem] = useState<string | null>(null);

  /** Billing session for Autocomplete (New). Reset after a place is resolved with fetchFields. */
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  /** Maps placeId → PlacePrediction from the latest suggestion fetch (for toPlace() + session). */
  const placePredictionByIdRef = useRef<Map<string, google.maps.places.PlacePrediction>>(new Map());
  const searchRequestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void tenantGoogleMapsLoader.isReady('places').then((ready) => {
      if (cancelled) return;
      setIsGoogleLoaded(ready);
      setMapsBootstrapping(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const searchPredictionsNew = useCallback(async (input: string, requestId: number) => {
    await tenantGoogleMapsLoader.importLibrary('places');
    const { AutocompleteSessionToken, AutocompleteSuggestion } = (await google.maps.importLibrary(
      'places'
    )) as google.maps.PlacesLibrary;

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new AutocompleteSessionToken();
    }

    const request: google.maps.places.AutocompleteRequest = {
      input: input.trim(),
      sessionToken: sessionTokenRef.current,
      includedRegionCodes: input.length < 8 ? ['ca'] : ['ca', 'us', 'gb', 'au'],
      region: 'ca',
      origin: { lat: 44.2312, lng: -76.4860 },
      language: 'en',
    };

    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    if (requestId !== searchRequestIdRef.current) return;

    const rows: Prediction[] = [];
    placePredictionByIdRef.current.clear();

    for (const suggestion of suggestions) {
      const pp = suggestion.placePrediction;
      if (!pp) continue;

      const main = formattableTextToString(pp.mainText ?? pp.text);
      const secondary = formattableTextToString(pp.secondaryText);
      const description = secondary ? `${main}, ${secondary}` : main;

      placePredictionByIdRef.current.set(pp.placeId, pp);
      rows.push({
        place_id: pp.placeId,
        description,
        structured_formatting: {
          main_text: main || description,
          secondary_text: secondary,
        },
      });
    }

    const sorted = sortPredictionsCanadaFirst(rows);
    setSuggestionProblem(null);
    setPredictions(sorted);
    setShowDropdown(sorted.length > 0);
  }, []);

  const searchPredictionsLegacy = useCallback(async (input: string, requestId: number) => {
    await new Promise<void>((resolve) => {
      const service = new google.maps.places.AutocompleteService();
      const legacyRequest = {
        input,
        componentRestrictions: input.length < 8 ? { country: ['ca'] } : { country: ['ca', 'us', 'gb', 'au'] },
        types: ['address'] as ('address' | 'establishment' | 'geocode' | '(regions)' | '(cities)')[],
        location: new google.maps.LatLng(44.2312, -76.4860),
        radius: 50000,
        strictBounds: false,
      };

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      const timer = window.setTimeout(finish, 12000);

      service.getPlacePredictions(legacyRequest, (preds: Prediction[] | null, status: string) => {
        window.clearTimeout(timer);
        if (requestId !== searchRequestIdRef.current) {
          finish();
          return;
        }
        if (status === google.maps.places.PlacesServiceStatus.OK && preds) {
          setSuggestionProblem(null);
          setPredictions(sortPredictionsCanadaFirst(preds));
          setShowDropdown(true);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
        finish();
      });
    }).catch(() => {
      if (requestId === searchRequestIdRef.current) {
        setPredictions([]);
        setShowDropdown(false);
      }
    });
  }, []);

  const searchPredictions = useCallback(
    async (input: string) => {
      if (!isGoogleLoaded || !input.trim() || input.length < 3) {
        setPredictions([]);
        setShowDropdown(false);
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setIsSearching(true);

      const safetyTimer = window.setTimeout(() => {
        if (requestId === searchRequestIdRef.current) {
          setIsSearching(false);
        }
      }, 15000);

      try {
        await tenantGoogleMapsLoader.importLibrary('places');
        const placesLib = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
        const fetchSuggestions = (
          placesLib as {
            AutocompleteSuggestion?: { fetchAutocompleteSuggestions?: unknown };
          }
        ).AutocompleteSuggestion?.fetchAutocompleteSuggestions;

        if (typeof fetchSuggestions === 'function') {
          await searchPredictionsNew(input, requestId);
        } else {
          await searchPredictionsLegacy(input, requestId);
        }
      } catch {
        if (requestId === searchRequestIdRef.current) {
          setPredictions([]);
          setShowDropdown(false);
          setSuggestionProblem('Address suggestions are unavailable from this site. You can still type the address.');
        }
      } finally {
        window.clearTimeout(safetyTimer);
        if (requestId === searchRequestIdRef.current) {
          setIsSearching(false);
        }
      }
    },
    [isGoogleLoaded, searchPredictionsLegacy, searchPredictionsNew]
  );

  useEffect(() => {
    if (!userIsTyping || value.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void searchPredictions(value);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [value, searchPredictions, userIsTyping]);

  const selectPlace = async (placeId: string, description: string) => {
    if (!isGoogleLoaded) return;

    setIsSearching(true);
    setShowDropdown(false);
    setPredictions([]);
    setSelectedIndex(-1);

    const pp = placePredictionByIdRef.current.get(placeId);
    placePredictionByIdRef.current.clear();

    try {
      if (pp && typeof pp.toPlace === 'function') {
        const place = pp.toPlace();
        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress'],
        });

        const components = parseAddressFromGooglePlace(place as Parameters<typeof parseAddressFromGooglePlace>[0]);
        const formatted =
          (place as { formattedAddress?: string }).formattedAddress ??
          (place as { formatted_address?: string }).formatted_address;

        onChange(components.address || formatted || description);
        onAddressSelect(components);
        sessionTokenRef.current = null;
        setIsSearching(false);
        return;
      }

      // Legacy Place Details (callback API)
      await new Promise<void>((resolve) => {
        const service = new google.maps.places.PlacesService(document.createElement('div'));
        let done = false;
        const timer = window.setTimeout(() => {
          if (!done) {
            done = true;
            onChange(description);
            resolve();
          }
        }, 15000);

        service.getDetails(
          {
            placeId,
            fields: ['address_components', 'formatted_address'],
          },
          (place: google.maps.places.PlaceResult | null, status: string) => {
            if (done) return;
            done = true;
            window.clearTimeout(timer);

            if (status === google.maps.places.PlacesServiceStatus.OK && place) {
              const components = parseAddressFromGooglePlace(place as Parameters<typeof parseAddressFromGooglePlace>[0]);
              onChange(components.address || place.formatted_address || description);
              onAddressSelect(components);
            } else {
              onChange(description);
            }
            resolve();
          }
        );
      });
    } catch {
      onChange(description);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);
    setUserIsTyping(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          const prediction = predictions[selectedIndex];
          void selectPlace(prediction.place_id, prediction.description);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setUserIsTyping(true)}
          onBlur={() => {
            setTimeout(() => {
              setShowDropdown(false);
              setUserIsTyping(false);
            }, 150);
          }}
          className={className}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
        />

        {isSearching && value.length >= 3 && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
          </div>
        )}
      </div>

      {mapsBootstrapping && (userIsTyping || value.length > 0) ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Loading address search…</p>
      ) : null}

      {suggestionProblem ? <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">{suggestionProblem}</p> : null}

      {showDropdown && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-white/15 rounded-lg shadow-lg max-h-60 overflow-auto">
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void selectPlace(prediction.place_id, prediction.description);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-red-50 text-red-700' : 'text-gray-700'
              }`}
            >
              <div className="flex flex-col">
                <span className="font-medium text-sm">{prediction.structured_formatting.main_text}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{prediction.structured_formatting.secondary_text}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
